import React, { useEffect, useRef, useState } from "react";

import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";

import DataTable from "../components/DataTable";
import RegisterInformationSubtitle from "../components/RegisterInformationSubtitle";
import Highlighter from "react-highlight-words";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";

type DataIndex = keyof Types.InformationSubtitleDto;

function InformationSubtitleList({ informationTitleId }: { informationTitleId: string }) {
    useEffect(() => {
        requestGetInformationSubtitles(informationTitleId);
        setcurrentInformationSubtitle(undefined);
    }, [informationTitleId]);

    const alert = useAlert();

    const [informationSubtitles, setInformationSubtitles] = useState<Array<Types.InformationSubtitleDto>>();
    const [currentInformationSubtitle, setcurrentInformationSubtitle] = useState<Types.InformationSubtitleDto>();

    const [editMode, setEditMode] = useState<string>("none");
    const [openDeleteInformationSubtitle, setOpenDeleteInformationSubtitle] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.InformationSubtitleDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        검색
                    </Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                        초기화
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        필터
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        닫기
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.InformationSubtitleDto> = [
        {
            align: "center",
            title: String.informationSubtitleId,
            dataIndex: "informationSubtitleId",
            key: "informationSubtitleId",
            ...getColumnSearchProps("informationSubtitleId"),
        },
        {
            align: "center",
            title: String.informationSubtitle,
            dataIndex: "informationSubtitle",
            key: "informationSubtitle",
            ...getColumnSearchProps("informationSubtitle"),
        },
    ];

    const requestAxiosGetInformationSubtitles = async (informationTitleId: number) => {
        let response = await utilAxiosWithAuth().get(Request.INFORMATION_SUBTITLE_LIST_URL);
        if (informationTitleId !== undefined) {
            response = await utilAxiosWithAuth().get(Request.INFORMATION_SUBTITLE_LIST_URL + "?informationTitleId=" + informationTitleId);
        }

        // console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetInformationSubtitles,
        error: errorGetInformationSubtitles,
        data: resultGetInformationSubtitles,
        execute: requestGetInformationSubtitles,
    } = useAsyncAxios(requestAxiosGetInformationSubtitles);

    useEffect(() => {
        if (resultGetInformationSubtitles === null) return;

        setInformationSubtitles(resultGetInformationSubtitles.informationSubtitles);
        console.log(resultGetInformationSubtitles);
    }, [resultGetInformationSubtitles]);

    useEffect(() => {
        if (errorGetInformationSubtitles === null) return;

        alert.setAlert(errorGetInformationSubtitles.response.data.code, "Error", errorGetInformationSubtitles.response.data.message);
    }, [errorGetInformationSubtitles]);

    const requestAxiosRemoveInformationSubtitle = async (informationSubtitle: Types.InformationSubtitleDto) => {
        const response = await utilAxiosWithAuth().delete(Request.INFORMATION_SUBTITLE_URL + "/" + informationSubtitle?.informationSubtitleId);
        return response.data;
    };

    const {
        loading: loadingRemoveInformationSubtitle,
        error: errorRemoveInformationSubtitle,
        data: resultRemoveInformationSubtitle,
        execute: requestRemoveInformationSubtitles,
    } = useAsyncAxios(requestAxiosRemoveInformationSubtitle);

    useEffect(() => {
        if (resultRemoveInformationSubtitle === null) return;

        alert.setAlert(AlertType.SUCCESS, "주요 안내 부제 삭제 성공", "주요 안내 부제 삭제를 완료하였습니다.");

        requestGetInformationSubtitles();
    }, [resultRemoveInformationSubtitle]);

    useEffect(() => {
        if (errorRemoveInformationSubtitle === null) return;

        alert.setAlert(errorRemoveInformationSubtitle.response.data.code, "Error", errorRemoveInformationSubtitle.response.data.message);
    }, [errorGetInformationSubtitles]);

    useEffect(() => {
        requestGetInformationSubtitles();
    }, []);

    const onRow = (value: Types.InformationSubtitleDto) => {
        setEditMode("none");
        setcurrentInformationSubtitle(value);
    };

    const handleDeleteInformationSubtitle = () => {
        if (currentInformationSubtitle !== undefined) setOpenDeleteInformationSubtitle(true);
    };

    const handleDeleteInformationSubtitleOk = () => {
        setOpenDeleteInformationSubtitle(false);
        requestRemoveInformationSubtitles(currentInformationSubtitle);
    };

    const handleDeleteInformationSubtitleCancel = () => {
        setOpenDeleteInformationSubtitle(false);
    };

    const onRegisterInformationSubtitle = () => {
        requestGetInformationSubtitles();
        setEditMode("none");
    };

    return (
        <>
            <Row justify="space-between">
                <Col span={6}>
                    <Divider orientation="left">
                        <span>주요 안내 부제</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            editMode === "add" ? setEditMode("none") : setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button
                        onClick={() => {
                            editMode === "update" ? setEditMode("none") : setEditMode("update");
                        }}
                    >
                        {String.userUpdate}
                    </Button>
                    <Button onClick={() => handleDeleteInformationSubtitle()} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <DataTable hasScroll={false} columns={columns} rowKey="modelId" dataSource={informationSubtitles} onRow={onRow} />

            {editMode === "add" && <RegisterInformationSubtitle editMode={editMode} onRegisterInformationSubtitle={onRegisterInformationSubtitle} />}

            {editMode !== "add" && currentInformationSubtitle !== undefined && (
                <RegisterInformationSubtitle
                    editMode={editMode}
                    currentInformationSubtitle={currentInformationSubtitle}
                    onRegisterInformationSubtitle={onRegisterInformationSubtitle}
                />
            )}

            {openDeleteInformationSubtitle && (
                <Modal open={openDeleteInformationSubtitle} onOk={handleDeleteInformationSubtitleOk} onCancel={handleDeleteInformationSubtitleCancel}>
                    {currentInformationSubtitle?.informationSubtitle} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default InformationSubtitleList;
